import {
  appsBomApiGetDrawingSheet,
  appsBomApiListAssemblies,
  appsBomApiListDrawingFiles,
  appsBomApiListDrawingSheets,
  appsBomApiListGroups,
  appsBomApiListProjects,
  appsBomApiListUnits,
  getAppsBomApiGetDrawingSheetQueryKey,
  getAppsBomApiListAssembliesQueryKey,
  getAppsBomApiListDrawingFilesQueryKey,
  getAppsBomApiListDrawingSheetsQueryKey,
  getAppsBomApiListGroupsQueryKey,
  getAppsBomApiListProjectsQueryKey,
  getAppsBomApiListUnitsQueryKey,
} from '@/lib/api/generated/endpoints'
import type { AssemblyOut } from '@/lib/api/generated/types.ts/assemblyOut'
import type { DrawingFileOut } from '@/lib/api/generated/types.ts/drawingFileOut'
import type { DrawingSheetOut } from '@/lib/api/generated/types.ts/drawingSheetOut'
import type { NomenclatureGroupOut } from '@/lib/api/generated/types.ts/nomenclatureGroupOut'
import type { ProjectSchema } from '@/lib/api/generated/types.ts/projectSchema'
import type { StructureTreeOut } from '@/lib/api/generated/types.ts/structureTreeOut'
import type { UnitOfMeasureOut } from '@/lib/api/generated/types.ts/unitOfMeasureOut'
import { queryOptions } from '@tanstack/react-query'
import { partLabel } from '../lib/part-label'
import { nomenclatureTreeQuery } from './nomenclature-tree-query'

/**
 * Lazy autocomplete queries for FK-picker comboboxes. Each accepts a search
 * string `q`; backend filters server-side. Pickers in T12 will pass q via
 * `useDeferredValue`-debounced state. Materials are NOT here — global
 * material catalog already lives in `bom-query.ts::projectMaterialsQuery`
 * and is reused by MaterialPicker.
 *
 * orval's customFetch returns parsed JSON directly (per `lib/api/fetcher.ts`);
 * we cast at queryFn level — the runtime shape matches the *Out types.
 *
 * Key stability: each factory always passes a `{ q }`-shaped object to the
 * orval `get*QueryKey` helper, so `unitsQuery()` and `unitsQuery(undefined)`
 * hash identically (`q: undefined` → omitted from the URL). React Query's
 * stable serializer handles object key ordering for us.
 */

export const unitsQuery = (q?: string) =>
  queryOptions({
    queryKey: getAppsBomApiListUnitsQueryKey({ q }),
    queryFn: async ({ signal }) =>
      (await appsBomApiListUnits({ q }, { signal })) as unknown as UnitOfMeasureOut[],
    staleTime: 60_000,
  })

export const drawingFilesQuery = (projectId: number, q?: string) =>
  queryOptions({
    queryKey: getAppsBomApiListDrawingFilesQueryKey(projectId, { q }),
    queryFn: async ({ signal }) =>
      (await appsBomApiListDrawingFiles(
        projectId,
        { q },
        { signal },
      )) as unknown as DrawingFileOut[],
    staleTime: 60_000,
    enabled: Number.isFinite(projectId) && projectId > 0,
  })

export const drawingSheetsQuery = (fileId: number, q?: string) =>
  queryOptions({
    queryKey: getAppsBomApiListDrawingSheetsQueryKey(fileId, { q }),
    queryFn: async ({ signal }) =>
      (await appsBomApiListDrawingSheets(
        fileId,
        { q },
        { signal },
      )) as unknown as DrawingSheetOut[],
    staleTime: 60_000,
    enabled: Number.isFinite(fileId) && fileId > 0,
  })

export const drawingSheetByIdQuery = (sheetId: number | null | undefined) =>
  queryOptions({
    queryKey: getAppsBomApiGetDrawingSheetQueryKey(sheetId ?? 0),
    queryFn: async ({ signal }) =>
      (await appsBomApiGetDrawingSheet(sheetId as number, {
        signal,
      })) as unknown as DrawingSheetOut,
    staleTime: 5 * 60_000,
    enabled: Number.isFinite(sheetId) && (sheetId ?? 0) > 0,
  })

/**
 * Project-scoped active Assemblies for the parent-Asm picker. Used by
 * AssemblyForm (optional parent) and PartForm (required parent).
 */
export const assembliesQuery = (projectId: number) =>
  queryOptions({
    queryKey: getAppsBomApiListAssembliesQueryKey(projectId),
    queryFn: async ({ signal }) =>
      (await appsBomApiListAssemblies(projectId, {
        signal,
      })) as unknown as readonly AssemblyOut[],
    staleTime: 60_000,
    enabled: Number.isFinite(projectId) && projectId > 0,
  })

/**
 * Projects list — used by the global Nomenclature page's ProjectPicker (when
 * creating a Part/Assembly cross-project, the user must choose which project
 * the new entity belongs to).
 */
export const projectsQuery = () =>
  queryOptions({
    queryKey: getAppsBomApiListProjectsQueryKey(),
    queryFn: async ({ signal }) =>
      (await appsBomApiListProjects({ signal })) as unknown as readonly ProjectSchema[],
    staleTime: 5 * 60_000,
  })

/**
 * NomenclatureGroup list with optional `project_id` scope. Pass
 * `project_id: null` semantically by passing `undefined` and filtering
 * client-side, OR rely on the backend `project_id` query parameter when
 * scoping to a specific project. The list endpoint caps at 1000 — folder
 * counts in a single project are well below that.
 */
export const nomenclatureGroupsQuery = (params: {
  projectId?: number | null
}) =>
  queryOptions({
    queryKey: getAppsBomApiListGroupsQueryKey(
      params.projectId == null ? undefined : { project_id: params.projectId },
    ),
    queryFn: async ({ signal }) =>
      (await appsBomApiListGroups(
        params.projectId == null ? undefined : { project_id: params.projectId },
        { signal },
      )) as unknown as readonly NomenclatureGroupOut[],
    staleTime: 60_000,
  })

export interface ComponentCandidate {
  kind: 'DET' | 'ASM'
  id: number
  code: string
  name: string
}

/**
 * Project-scoped combined list of active Parts + Assemblies for the
 * component picker. Parents excluded by the consumer (the parent Asm being
 * edited can't be its own component — UI-side defence in depth even though
 * backend cycle check would 409 anyway).
 *
 * Derived from the structure tree rather than the parts/assemblies lists:
 * the tree is already cached on every page that edits composition, carries
 * only active rows, is invalidated by every BOM mutation, and has the
 * position and section a part is known by («поз. 18 · Лист · 10x199»).
 * Order stays Parts first, then Assemblies.
 */
export function treeComponentCandidates(tree: StructureTreeOut): ComponentCandidate[] {
  return [
    ...tree.parts
      .filter((p) => p.is_active)
      .map(
        (p): ComponentCandidate => ({ kind: 'DET', id: p.id, code: p.code, name: partLabel(p) }),
      ),
    ...tree.assemblies
      .filter((a) => a.is_active)
      .map((a): ComponentCandidate => ({ kind: 'ASM', id: a.id, code: a.code, name: a.name })),
  ]
}

export function componentCandidatesQuery(projectId: number) {
  return queryOptions({ ...nomenclatureTreeQuery(projectId), select: treeComponentCandidates })
}
